import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Building2, Layers, Sparkles, Compass, ShieldCheck, MapPin } from "lucide-react";
import ContactForm from "../components/ContactForm";

export default function Home() {
  const { t } = useTranslation();

  const goTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="ec-home">
      {/* HERO */}
      <section className="ec-hero">
        <div className="ec-hero-grid">
          <div className="ec-hero-copy">
            <span className="ec-eyebrow">{t("hero.eyebrow")}</span>
            <h1 className="ec-h1">
              <span>{t("hero.title_1")}</span>
              <span className="ec-h1-accent">{t("hero.title_2")}</span>
            </h1>
            <p className="ec-lead">{t("hero.subtitle")}</p>
            <div className="ec-hero-cta">
              <a href="#projects" onClick={goTo("projects")} className="ec-btn ec-btn--primary">
                {t("hero.cta_primary")} <ArrowRight size={16} />
              </a>
              <a href="#contact" onClick={goTo("contact")} className="ec-btn ec-btn--ghost">
                {t("hero.cta_secondary")}
              </a>
            </div>
            <dl className="ec-hero-stats">
              <div>
                <dt>{t("hero.stat_1_v")}</dt>
                <dd>{t("hero.stat_1")}</dd>
              </div>
              <div>
                <dt>{t("hero.stat_2_v")}</dt>
                <dd>{t("hero.stat_2")}</dd>
              </div>
              <div>
                <dt>{t("hero.stat_3_v")}</dt>
                <dd>{t("hero.stat_3")}</dd>
              </div>
            </dl>
          </div>

          <div className="ec-hero-visual">
            <div className="ec-hero-photo ec-hero-photo--main">
              <img
                src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80"
                alt=""
              />
            </div>
            <div className="ec-hero-photo ec-hero-photo--sub">
              <img
                src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=800&q=80"
                alt=""
              />
            </div>
            <div className="ec-hero-chip">
              <MapPin size={14} /> Milano · Roma · Torino
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="ec-intro">
        <div className="ec-container ec-intro-inner">
          <h2 className="ec-h2">{t("intro.title")}</h2>
          <p className="ec-para">{t("intro.body")}</p>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="benefits" className="ec-benefits">
        <div className="ec-container">
          <div className="ec-section-head">
            <span className="ec-eyebrow">{t("nav.benefits")}</span>
            <h2 className="ec-h2">{t("benefits.title")}</h2>
            <p className="ec-sub">{t("benefits.subtitle")}</p>
          </div>
          <div className="ec-benefits-grid">
            <BenefitCard icon={<Compass size={22} />} title={t("benefits.items.barriers.title")} body={t("benefits.items.barriers.body")} />
            <BenefitCard icon={<Layers size={22} />} title={t("benefits.items.digital.title")} body={t("benefits.items.digital.body")} />
            <BenefitCard icon={<Building2 size={22} />} title={t("benefits.items.choice.title")} body={t("benefits.items.choice.body")} />
            <BenefitCard icon={<Sparkles size={22} />} title={t("benefits.items.impact.title")} body={t("benefits.items.impact.body")} />
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="how" className="ec-how">
        <div className="ec-container">
          <div className="ec-section-head">
            <span className="ec-eyebrow">{t("nav.how")}</span>
            <h2 className="ec-h2">{t("how.title")}</h2>
            <p className="ec-sub">{t("how.subtitle")}</p>
          </div>
          <ol className="ec-how-list">
            {["one", "two", "three", "four"].map((k) => (
              <li className="ec-how-item" key={k}>
                <span className="ec-how-num">{t(`how.steps.${k}.num`)}</span>
                <h3>{t(`how.steps.${k}.title`)}</h3>
                <p>{t(`how.steps.${k}.body`)}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="ec-projects">
        <div className="ec-container">
          <div className="ec-section-head">
            <span className="ec-eyebrow">{t("nav.projects")}</span>
            <h2 className="ec-h2">{t("projects.title")}</h2>
            <p className="ec-sub">{t("projects.subtitle")}</p>
          </div>
          <div className="ec-projects-grid">
            <ProjectCard
              img="https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=1000&q=80"
              tag={t("projects.items.one.tag")}
              title={t("projects.items.one.title")}
              body={t("projects.items.one.body")}
              cta={t("projects.cta")}
              onCta={goTo("contact")}
            />
            <ProjectCard
              img="https://images.unsplash.com/photo-1523217582562-09d0def993a6?auto=format&fit=crop&w=1000&q=80"
              tag={t("projects.items.two.tag")}
              title={t("projects.items.two.title")}
              body={t("projects.items.two.body")}
              cta={t("projects.cta")}
              onCta={goTo("contact")}
            />
            <ProjectCard
              img="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1000&q=80"
              tag={t("projects.items.three.tag")}
              title={t("projects.items.three.title")}
              body={t("projects.items.three.body")}
              cta={t("projects.cta")}
              onCta={goTo("contact")}
            />
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section id="transparency" className="ec-transparency">
        <div className="ec-container ec-trans-grid">
          <div className="ec-trans-copy">
            <span className="ec-eyebrow">{t("nav.transparency")}</span>
            <h2 className="ec-h2">{t("transparency.title")}</h2>
            <p className="ec-para">{t("transparency.body_1")}</p>
            <p className="ec-para">{t("transparency.body_2")}</p>
            <p className="ec-para">{t("transparency.body_3")}</p>
            <ul className="ec-trans-points">
              {["one", "two", "three", "four"].map((k) => (
                <li key={k}>
                  <ShieldCheck size={16} />
                  <span>{t(`transparency.points.${k}`)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="ec-trans-visual">
            <img
              src="https://images.unsplash.com/photo-1502005229762-cf1b2da7c5d6?auto=format&fit=crop&w=1000&q=80"
              alt=""
            />
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="ec-contact">
        <div className="ec-container ec-contact-grid">
          <div className="ec-contact-copy">
            <span className="ec-eyebrow">{t("nav.contact")}</span>
            <h2 className="ec-h2">{t("contact.title")}</h2>
            <p className="ec-sub">{t("contact.subtitle")}</p>
          </div>
          <div className="ec-contact-form-wrap">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}

function BenefitCard({ icon, title, body }) {
  return (
    <div className="ec-benefit">
      <div className="ec-benefit-icon">{icon}</div>
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

function ProjectCard({ img, tag, title, body, cta, onCta }) {
  return (
    <article className="ec-project">
      <div className="ec-project-img">
        <img src={img} alt="" />
      </div>
      <div className="ec-project-body">
        <span className="ec-tag">{tag}</span>
        <h3>{title}</h3>
        <p>{body}</p>
        <a href="#contact" onClick={onCta} className="ec-project-cta">
          {cta} <ArrowRight size={14} />
        </a>
      </div>
    </article>
  );
}
